import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from "react";
import { authService } from "../apis/authService";
import {
  prefetchUserAudioFiles,
  clearAudioInventory,
} from "../hooks/useAudioCache";

const AuthContext = createContext(null);

function resolveUserId(user) {
  if (!user) return null;
  const id = user.userId ?? user.userid ?? user.id ?? null;
  return id != null ? String(id) : null;
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentUserId = useMemo(() => resolveUserId(currentUser), [currentUser]);

  useEffect(() => {
    let alive = true;
    const bootstrapSession = async () => {
      setLoading(true);
      try {
        let me = await authService.getMe();

        if (!me.success) {
          const refreshed = await authService.refreshToken();
          if (refreshed.success) {
            me = await authService.getMe();
          }
        }

        if (alive) {
          setCurrentUser(me.success ? me.data : null);
        }
      } catch (err) {
        console.warn("Auth bootstrap error:", err);
        if (alive) setCurrentUser(null);
      } finally {
        if (alive) setLoading(false);
      }
    };

    bootstrapSession();
    return () => {
      alive = false;
    };
  }, []);

  // Map available S3 audio files right after login / session restore.
  // Do not clear on the initial null bootstrap — that would wipe sessionStorage
  // before getMe() restores the user.
  const prevUserIdRef = React.useRef(undefined);
  useEffect(() => {
    const prev = prevUserIdRef.current;
    prevUserIdRef.current = currentUserId;

    if (!currentUserId) {
      if (prev) {
        clearAudioInventory(prev);
      }
      return;
    }
    prefetchUserAudioFiles({ force: true, userId: currentUserId }).catch(() => {});
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUser) return;

    const refreshTokenInterval = setInterval(async () => {
      try {
        const result = await authService.refreshToken();
        if (!result?.success) {
          await logout();
        }
      } catch {
        await logout();
      }
    }, 55 * 60 * 1000); // refresh a few minutes before 1h access token expiry

    return () => clearInterval(refreshTokenInterval);
  }, [currentUser]);

  const refreshMe = useCallback(async () => {
    try {
      const result = await authService.getMe();
      if (result.success && result.data) {
        setCurrentUser(result.data);
        return true;
      }
      setCurrentUser(null);
      return false;
    } catch (err) {
      console.warn("Auth refresh error:", err);
      setCurrentUser(null);
      return false;
    }
  }, []);
  const googleAuthenticate = refreshMe; // backward-compatible alias

  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const result = await authService.login(email, password);
      console.log("Login result:", result.data.email);
      if (result.success) {
        setCurrentUser({
          email: result.data.email,
          // If name isn't available, use email as fallback
          name: result.data.name || email.split("@")[0],
          isAdmin: result.data.isAdmin || false,
          role: result.data.role,
          userid: result.data.userid,
        });
        return { success: true };
      } else {
        setError(result.message);
        return { success: false, message: result.message };
      }
    } catch (err) {
      const errorMessage = err.message || "An unexpected error occurred";
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (name, email, password) => {
    setLoading(true);
    setError(null);
    try {
      const result = await authService.signup(name, email, password);
      console.log("Login result:", result.data.email);

      if (result.success) {
        setCurrentUser({
          email: result.data.email,
          name: result.data.name,
        });
        return { success: true };
      } else {
        setError(result.message);
        return { success: false, message: result.message };
      }
    } catch (err) {
      setError("An unexpected error occurred");
      return { success: false, message: "An unexpected error occurred" };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    clearAudioInventory(currentUserId);
    setCurrentUser(null);
    await authService.logout();
    setLoading(false);
  };

  const value = {
    currentUser,
    loading,
    error,
    googleAuthenticate,
    refreshMe,
    login,
    signup,
    logout,
    isAuthenticated: !!currentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
